import { useState, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

const TEAL = "#6FAFB5";
const NAVY = "#1F2E3B";
const CREAM = "#F7F4EE";
const MUTED = "#536270";

function parseOutput(text: string) {
  const lines = text.split("\n");
  const result: Array<{ heading: boolean; text: string }> = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) { result.push({ heading: false, text: "" }); continue; }
    if (
      trimmed.startsWith("##") ||
      /^[A-Z][A-Z\s:&/\-]{5,}$/.test(trimmed) ||
      /^\*\*[^*]+\*\*$/.test(trimmed)
    ) {
      result.push({ heading: true, text: trimmed.replace(/^#+\s*/, "").replace(/\*\*/g, "") });
    } else {
      result.push({ heading: false, text: trimmed });
    }
  }
  return result;
}

const PRESETS = [
  {
    label: "Website copy brief",
    prompt: "Write a structured website copy brief for a [business type] called [business name] based in [location]. Include: headline, sub-headline, 3 key value propositions, services section copy, about section, and a call to action.",
  },
  {
    label: "Proposal email",
    prompt: "Write a short, warm, personalised proposal email from Donna at BizDr to [contact name] at [business name]. Mention their current online presence (or lack of it), what a professional website could do for them, and propose a free no-obligation demo. Keep it under 200 words.",
  },
  {
    label: "SEO page outline",
    prompt: "Create a detailed SEO content outline for a blog post targeting the keyword '[keyword]'. Include: title, meta description, H1, H2 sections with dot-point content notes, and a suggested word count.",
  },
  {
    label: "Google Business Profile",
    prompt: "Write an optimised Google Business Profile description for [business name], a [business type] based in [location]. Include main services, key selling points, and a call to action. Max 750 characters.",
  },
];

export default function AdminReport() {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState(false);

  const [prompt, setPrompt] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [showSystem, setShowSystem] = useState(false);
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState<string | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  function handleAuth(e: React.FormEvent) {
    e.preventDefault();
    fetch("/api/admin/auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.ok) { setAuthed(true); setAuthError(false); }
        else { setAuthError(true); }
      })
      .catch(() => setAuthError(true));
  }

  async function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    if (!prompt.trim()) return;
    setLoading(true);
    setOutput(null);
    try {
      const res = await fetch("/api/generate-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, prompt: prompt.trim(), systemPrompt: systemPrompt.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Generation failed");
      setOutput(data.result);
      setTimeout(() => outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (err: any) {
      toast({ title: "Generation failed", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  function copyOutput() {
    if (!output) return;
    navigator.clipboard.writeText(output);
    toast({ title: "Copied", description: "Output copied to clipboard." });
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
        <div className="w-full max-w-sm p-8 rounded-2xl" style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
          <div className="flex items-center gap-2 mb-6">
            <svg width="24" height="14" viewBox="0 0 28 16" fill="none">
              <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.3rem", color: NAVY }}>
              Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
            </span>
            <span className="ml-2 text-xs font-medium" style={{ color: MUTED }}>Admin</span>
          </div>
          <h1 className="text-base font-semibold mb-5" style={{ color: NAVY }}>AI Generator — Private Access</h1>
          <form onSubmit={handleAuth} className="flex flex-col gap-4">
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              className="w-full rounded-xl px-4 py-3 text-sm outline-none border"
              style={{ borderColor: authError ? "#e05" : "#d6cfc5", background: CREAM, color: NAVY }}
            />
            {authError && <p className="text-xs" style={{ color: "#c0392b" }}>Incorrect password.</p>}
            <button type="submit" className="rounded-full py-3 text-sm font-medium"
              style={{ background: NAVY, color: "#fff" }}>
              Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: CREAM, fontFamily: "'DM Sans', sans-serif", color: NAVY }}>
      <header style={{ background: "rgba(247,244,238,.95)", backdropFilter: "blur(14px)", borderBottom: "1px solid #e4ddd2", position: "sticky", top: 0, zIndex: 50 }}>
        <div className="mx-auto max-w-7xl px-6 py-3 flex items-center gap-3 flex-wrap">
          <svg width="24" height="14" viewBox="0 0 28 16" fill="none">
            <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.3rem", color: NAVY }}>
            Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
          </span>
          <span className="text-xs px-2 py-1 rounded-full ml-1 font-medium" style={{ background: "#e4ddd2", color: MUTED }}>
            AI Generator
          </span>
          <div className="ml-auto flex gap-4 text-xs" style={{ color: MUTED }}>
            <a href="/admin/research" className="underline">Business Research</a>
            <a href="/admin/clubs" className="underline">Clubs Research</a>
            <a href="/admin/marketing" className="underline">Marketing Intel</a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-8">
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.8rem", fontWeight: 600, color: NAVY, marginBottom: 4 }}>
            AI Generator
          </h1>
          <p style={{ color: MUTED, fontSize: "0.9rem" }}>Type any prompt — or pick a preset to get started quickly.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">

          {/* Left: Prompt panel */}
          <form onSubmit={handleGenerate} className="flex flex-col gap-4 rounded-2xl p-7" style={{ background: "#fff", border: "1px solid #e4ddd2" }}>

            {/* Presets */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: MUTED }}>Quick presets</p>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map(p => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setPrompt(p.prompt)}
                    className="text-xs px-3 py-1.5 rounded-full border transition-all hover:-translate-y-0.5"
                    style={{ borderColor: "#d6cfc5", color: NAVY, background: CREAM }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium" style={{ color: NAVY }}>Your prompt</label>
              <textarea
                rows={10}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Type anything — describe what you want Claude to write, research, or summarise. Fill in the [bracketed placeholders] if you used a preset."
                className="w-full rounded-xl px-4 py-3 text-sm outline-none border resize-vertical transition-all"
                style={{ borderColor: "#d6cfc5", background: "#fff", color: NAVY, fontFamily: "DM Sans, sans-serif", minHeight: 200 }}
                onFocus={e => (e.target.style.borderColor = TEAL)}
                onBlur={e => (e.target.style.borderColor = "#d6cfc5")}
              />
            </div>

            {/* Optional system prompt toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowSystem(s => !s)}
                className="text-xs underline"
                style={{ color: MUTED }}
              >
                {showSystem ? "Hide" : "Add"} custom system prompt (optional)
              </button>
              {showSystem && (
                <textarea
                  rows={4}
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  placeholder="e.g. You are a copywriter for Australian small businesses. Write in plain, warm, direct language. Never use jargon."
                  className="mt-2 w-full rounded-xl px-4 py-3 text-sm outline-none border resize-vertical transition-all"
                  style={{ borderColor: "#d6cfc5", background: CREAM, color: NAVY, fontFamily: "DM Sans, sans-serif", minHeight: 90 }}
                  onFocus={e => (e.target.style.borderColor = TEAL)}
                  onBlur={e => (e.target.style.borderColor = "#d6cfc5")}
                />
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !prompt.trim()}
              className="mt-1 w-full rounded-full py-4 text-base font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: TEAL, color: "#fff" }}>
              {loading ? "Generating…" : "Generate"}
            </button>
          </form>

          {/* Right: Output panel */}
          <div ref={outputRef} className="rounded-2xl p-7 min-h-96" style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <h2 className="font-semibold text-base" style={{ color: NAVY }}>Output</h2>
              {output && (
                <button onClick={copyOutput}
                  className="rounded-full px-4 py-2 text-xs font-medium transition-all hover:-translate-y-0.5"
                  style={{ background: TEAL, color: "#fff" }}>
                  Copy Output
                </button>
              )}
            </div>

            {loading && (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="animate-spin rounded-full border-4 border-t-transparent"
                  style={{ width: 44, height: 44, borderColor: `${TEAL} transparent ${TEAL} ${TEAL}` }} />
                <p className="text-sm" style={{ color: MUTED }}>Generating…</p>
              </div>
            )}

            {!loading && !output && (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div style={{ fontSize: "2.5rem" }}>✨</div>
                <p className="text-sm text-center" style={{ color: MUTED }}>Your output will appear here. Use a preset or write your own prompt.</p>
              </div>
            )}

            {!loading && output && (
              <div className="text-sm leading-relaxed" style={{ color: NAVY }}>
                {parseOutput(output).map((block, i) => {
                  if (!block.text) return <div key={i} className="h-3" />;
                  if (block.heading) {
                    return (
                      <h3 key={i} className="font-semibold text-sm uppercase tracking-wide mt-6 mb-2 pb-1"
                        style={{ color: TEAL, borderBottom: `1px solid #e4ddd2`, letterSpacing: "0.08em" }}>
                        {block.text}
                      </h3>
                    );
                  }
                  const text = block.text
                    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                    .replace(/\*(.*?)\*/g, "<em>$1</em>");
                  return <p key={i} className="mb-1" style={{ lineHeight: 1.75 }} dangerouslySetInnerHTML={{ __html: text }} />;
                })}
              </div>
            )}
          </div>

        </div>
      </main>
    </div>
  );
}
